import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Skull,
  Pill,
  Heart,
  Activity,
  Droplet,
  AlertTriangle,
  Zap,
  Sparkles,
  Loader2,
  CheckCircle2,
  Shield
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsIntoxPage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (intoxId, intoxName, content) => {
    setAiLoading({ ...aiLoading, [intoxId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_toxicologie',
          audience: 'adult',
          prompt: `Analyse toxicologique pour ${intoxName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Mécanisme d'action et toxicité
2. Signes cliniques précoces à rechercher
3. Antidote spécifique et posologie
4. Critères de gravité nécessitant réanimation
5. Pièges diagnostiques et thérapeutiques`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [intoxId]: data.response });
        toast.success(`Analyse AI ${intoxName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [intoxId]: false });
    }
  };

  const intoxications = [
    {
      id: 'toxidromes',
      name: 'Toxidromes',
      icon: Activity,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**TOXIDROMES - SYNDROMES TOXIQUES**

**DÉFINITION:**
Ensemble de signes cliniques caractéristiques permettant d'orienter le diagnostic étiologique d'une intoxication.

**ÉVALUATION CLINIQUE:**

**1. ÉTAT DE VIGILANCE:**
• **Calme/Sédation:**
  - Opiacés
  - Benzodiazépines
  - Barbituriques
  - Phénothiazines sédatives
  - Valproate de sodium

• **Agitation:**
  - Antidépresseurs tricycliques
  - Cocaïne, Amphétamines
  - Anticholinergiques
  - Antihistaminiques
  - Hypoglycémie
  - Éthanol, CO

**2. PUPILLES:**
• **Myosis serré:**
  - Opiacés
  - Organophosphorés/carbamates
  - Phénothiazines (Chlorpromazine, etc.)

• **Mydriase peu réactive:**
  - Tricycliques, ISRS
  - Cocaïne, Amphétamines
  - Sympathomimétiques
  - Carbamazépine
  - Atropine
  - Méthanol
  - Cannabis

**3. RESPIRATION:**
• **Hypopnée/Apnée brutale:**
  - Opiacés
  - Barbituriques
  - Cyanure, H2S
  - Curares
  - Anticholinestérasiques
  - Acidose métabolique
  - Inhalation gaz toxiques

• **Polypnée:**
  - Sans cyanose: détresse modérée
  - Avec cyanose/OAP: détresse sévère

**4. SIGNES NEUROLOGIQUES:**

**SYNDROME PYRAMIDAL:**
• Hypertonie, spasticité
• Réflexes vifs

**SYNDROME EXTRAPYRAMIDAL:**
• Hypertonie, akinésie
• Tremblements

**SYNDROME CHOLINERGIQUE:**
• **Muscarinique (SLUDGE):**
  - Salivation
  - Lacrimation
  - Urination
  - Défécation
  - GI distress (nausées, vomissements)
  - Emesis
• **Nicotinique:**
  - Fasciculations
  - Paralysie musculaire

**SYNDROME ANTICHOLINERGIQUE:**
• Sécheresse cutanéo-muqueuse
• Hyperthermie
• Mydriase
• Tachycardie
• Rétention urinaire
• Diminution transit
• Confusion, hallucinations

**SYNDROME ADRÉNERGIQUE:**
• **Alpha:**
  - Hypertension
  - Vasoconstriction
• **Beta:**
  - Tachycardie
  - Bronchodilatation
  - Hyperglycémie

**SYNDROME SÉROTONINERGIQUE:**
(≥ 3 signes parmi:)
• Agitation, confusion
• Myoclonies
• Hyperréflexie, clonus
• Hypersudation
• Hyperthermie
• Diarrhée
• Tremblement

**DÉMARCHE DIAGNOSTIQUE:**
1. Identifier le syndrome toxique dominant
2. Rechercher les toxiques compatibles
3. Traitement symptomatique urgent
4. Antidote spécifique si disponible
5. Centre antipoison si doute`
    },
    {
      id: 'stabilisants-membrane',
      name: 'Stabilisants de Membrane',
      icon: Heart,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**INTOXICATION AUX STABILISANTS DE MEMBRANE**

**MÉCANISME:**
• Blocage des canaux sodium (phase 0)
• Blocage des canaux potassium (phase 3)

**TOXIQUES CONCERNÉS (non exhaustif):**
• Antidépresseurs tricycliques/tétracycliques
• Phénothiazines
• Chloroquine
• Bêtabloquants (Propranolol, Acébutolol)
• Antiarythmiques classe I
• Cocaïne

**SIGNES CLINIQUES:**

**CARDIO:**
• Bradycardie
• Troubles du rythme
• Hypotension, choc (vasoplégique ou cardiogénique)

**NEURO:**
• Convulsions
• Coma (surtout Propranolol par lipophilie)
• Dépression respiratoire

**RESPIRATOIRE:**
• Dépression respiratoire
• SDRA retardé (12-48h)
• Inhalation

**ECG - SIGNES MAJEURS:**
• **Dose faible:**
  - Aplatissement onde T
  - Allongement QTc

• **Dose élevée:**
  - Élargissement QRS (qR en aVR)
  - R > 3mm ou R/S > 0,7 en aVR (typique tricycliques)
  - Aspect de Brugada
  - Bloc AV

• **Risque:** TV, TSV, BAV haut degré, asystolie

**BIOLOGIE:**
• Hypokaliémie de transfert
• Acidose lactique (bas débit)

**TRAITEMENT:**

**SYMPTOMATIQUE INITIAL:**
• **Charbon activé:** si ingestion < 1-2h
• **Expansion volémique:** NaCl 0,9% 500-1000 mL
• **Bicarbonate de sodium 84‰:**
  - 250 mL sur 30 min
  - Renouvelable 2 fois
  - **Indication:** QRS > 0,12s
• **IOT/VM:** si défaillance neurologique

**TRAITEMENT SPÉCIFIQUE:**
• **Amines vasoactives:** selon profil hémodynamique
• **Assistance circulatoire:** discuter ECMO si:
  - ACR réfractaire
  - Choc cardiogénique réfractaire

**CAS PARTICULIER - CHLOROQUINE:**
• **EN PLUS du traitement ci-dessus:**
• **Diazépam:**
  - 2 mg/kg IV sur 30 min (d'où IOT)
  - Puis 2-4 mg/kg/j
• **Adrénaline:** en 1ère intention si amines nécessaires

**ORIENTATION:**
• Transport médicalisé
• Réanimation ou CHU selon régulation

**⚠️ À SAVOIR:**
• Surveillance ECG continue
• Risque d'aggravation rapide
• Bicarbonates = traitement clé
• ECMO précoce si choc réfractaire`
    },
{
      id: 'cardiotropes',
      name: 'Cardiotropes (BB & ICa)',
      icon: Heart,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**INTOXICATION AUX CARDIOTROPES**
**Bêtabloquants & Inhibiteurs Calciques**

**SIGNES CLINIQUES:**

**CARDIO:**
• Hypotension/Bradycardie
• Choc (vasoplégique ou cardiogénique)

**NEURO:**
• Convulsions/Coma (Propranolol: lipophilie + ESM)
• Dépression respiratoire

**RESPIRATOIRE:**
• Propranolol, Timolol, Sotalol
• Bronchoconstriction
• Toxicité pulmonaire ICa → SDRA

**ECG:**
• Tachycardie réflexe (possible avec DHP)
• Bradycardies (sinusale, BAV, BSA, BIB)
• Arythmies (ESV, TV, FV)
• QT prolongé → Torsades de Pointes/TV
• Effet stabilisant membrane → QRS large

**BIOLOGIE:**

**Bêtabloquants:**
• Hypokaliémie si ESM
• Hyperkal​émie rare
• Hypoglycémie (nourrisson)

**ICa:**
• Hyperglycémie (insuffisance pancréas, insulino-résistance)
• Acidose métabolique (lactique, cétonique)
• Insuffisance rénale

**TRAITEMENT:**

**BRADYCARDIE ISOLÉE (QRS fin, sans hypotension):**
• **Atropine:** 0,5 mg IV bolus si FC < 60
  - Toutes les 3-5 min, max 1,5 mg
• **Isoprénaline:** 1-5 mg/h
  - Objectif FC > 70/min
• **Entraînement électrosystolique externe:**
  - Si échec atropine/isoprénaline

**SI EFFET STABILISANT MEMBRANE (QRS ≥ 120ms):**
• **Bicarbonate 84‰:**
  - 1-2 mL/kg (max 250 mL si < 20kg)
  - Adapter dose pour QRS < 120ms et/ou ↑TA

**SI HYPOTENSION (PAS < 100 mmHg):**
• **Expansion volémique:**
  - NaCl 0,9%: 500-1000 mL

• **Hypotension avec Tachycardie & QRS fin:**
  - NORADRÉNALINE

• **Autres situations:**
  - ADRÉNALINE

**BÊTABLOQUANTS - Traitement spécifique:**
• **GLUCAGON:** en association avec amines
  - Bolus: 5-10 mg IV (0,05-0,15 mg/kg max 1 mg enfant)
  - Si efficace: IVSE 10 mg/h (0,1 mg/kg/h enfant)

**INHIBITEURS CALCIQUES - Traitement spécifique:**
• **Chlorure de calcium 10%:**
  - 10 mL IV sur 1-2 min
  - Répéter toutes les 2-3 min, max 50 mL
  - Puis IVSE 10 mL/h
  - Surveiller calcémie ionisée

**CHOC CARDIOGÉNIQUE RÉFRACTAIRE:**
• Assistance circulatoire/ECMO à discuter

**ORIENTATION:**
• Transport médicalisé
• CH St Brieuc`
    },
    {
      id: 'digitaliques',
      name: 'Digitaliques',
      icon: Heart,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**INTOXICATION AUX DIGITALIQUES**

**MÉCANISME:**
• Inhibiteur de la Na/K-ATPase

**ORIGINE:**
• Médicaments (Digoxine)
• Plantes: Digitale, Laurier rose, Muguet, Hellébore

**DOSE TOXIQUE:**
• > 2 mg (patient cardiaque)
• > 5 mg (cœur sain)

**SIGNES CLINIQUES:**

**CARDIO:**
• Bradycardie
• Asystolie
• TV et FV
• Choc cardiogénique

**DIGESTIF (80%):**
• Vomissements
• Diarrhée
• Douleurs abdominales

**NEURO:**
• Confusion
• Agitation

**OPHTALMO:**
• Vision trouble
• Dyschromatopsie (vision jaune/verte)

**ECG - EXAMEN CLÉ:**

**Imprégnation digitalique:**
• Aplatissement onde T
• Onde T négative
• Sous-décalage point J
• QTc raccourci

**Troubles automatisme:**
• ESA, Tachycardie atriale
• Flutter, Jonctionnelle
• ESV, TV, FV

**Troubles conduction:**
• Bradycardie sinusale
• BAV
• Rythme jonctionnel d'échappement

**Typique:** Bradycardie sinusale + BAV
OU Rythme jonctionnel d'échappement

**BIOLOGIE:**
• Hyperkaliémie
• Insuffisance rénale
• Digoxinémie (interprétable après 4h)

**TRAITEMENT:**

**SYMPTOMATIQUE INITIAL:**
• VVP
• Scope
• ETT
• **Charbon activé:** si ingestion < 2h
• Oxygène
• IOT si nécessaire

**ATROPINE:**
• Si bradycardie < 50/min
• Différencier origine vagale vs BAV de haut degré

**ENTRAÎNEMENT ÉLECTROSYSTOLIQUE:**
• Si échec antiarythmiques et bradycardie

**⚠️ CONTRE-INDICATIONS:**
• **Calcium IV:** PROSCRIT
• **Isoprénaline:** contre-indiquée

**TRAITEMENT SPÉCIFIQUE - DIGIFAB®:**
• **Appel Centre Antipoison Angers**
• Indication et posologie à discuter

**NEUTRALISATION SEMI-MOLAIRE:**
• Débuter si ≥ 3 facteurs pronostiques:
  - Âge > 55 ans
  - Homme
  - Cardiopathie préexistante
  - BAV
  - Bradycardie résistante à 1 mg Atropine
  - K+ > 4,5 mmol/L

**NEUTRALISATION MOLAIRE/CURATIVE:**
• Au moins 1 facteur de risque:
  - Arythmie ventriculaire
  - Bradycardie < 40/min résistante à 1 mg Atropine
  - K+ > 5,5 mmol/L
  - Choc cardiogénique
  - Infarctus mésentérique

**ORIENTATION:**
• Transport médicalisé
• CH St Brieuc (USIC ou Réanimation)

**⚠️ À SAVOIR:**
• 2ème dose DIGIFAB peut être nécessaire
• Risque de redigitalisation
• Surveillance prolongée nécessaire`
    },
    {
      id: 'paracetamol',
      name: 'Paracétamol',
      icon: Pill,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**INTOXICATION AU PARACÉTAMOL**

**ÉPIDÉMIOLOGIE:**
• Intoxication fréquente
• Risque mortel sans traitement (hépatite fulminante)

**DOSE TOXIQUE:**
• **Hépatotoxicité:** > 100 mg/kg
• **Risque létal:** > 10 g (adulte)

**FACTEURS DE RISQUE:**
• **Inducteurs enzymatiques:**
  - Alcool chronique
  - Antiépileptiques
• **Déplétion glutathion:**
  - Anorexie, malnutrition
  - Antiviraux

**SIGNES CLINIQUES:**

**PHASE PRÉCOCE (< 24h):**
• Symptômes digestifs:
  - Nausées
  - Vomissements
  - Douleurs abdominales

**PHASE TARDIVE (24-72h):**
• **Hépatite cytolytique**
• **Insuffisance hépatique aiguë sévère:**
  - TP < 50%
  - Encéphalopathie associée
• **Défaillance hémodynamique**
• **Insuffisance rénale**
• **Acidose lactique**

**BIOLOGIE:**

**PARACÉTAMOLÉMIE:**
• Valeur pronostique
• **Nomogramme de Rumack-Matthew**
• ⚠️ Non interprétable avant 4h
• Répéter si prise répétée

**AUTRES:**
• Transaminases (ASAT, ALAT)
• TP, Facteur V
• Bilirubine
• Créatinine
• Lactates

**TRAITEMENT:**

**CHARBON ACTIVÉ:**
• Si ingestion < 1h
• Absence de contre-indications

**ANTIDOTE - N-ACÉTYLCYSTÉINE IV:**

**INDICATION:**
• Immédiatement si dose toxique suspectée/confirmée
• Ne pas attendre résultat paracétamolémie

**PROTOCOLE:**
• **Dose de charge:**
  - 150 mg/kg dans 250 mL G5%
  - Sur 60 minutes

• **Dose intermédiaire:**
  - 50 mg/kg dans 500 mL G5%
  - Sur 4 heures

• **Dose d'entretien:**
  - 100 mg/kg dans 1000 mL G5%
  - Sur 16 heures

• **Poursuite si nécessaire:**
  - 100 mg/kg/jour
  - Jusqu'à paracétamolémie négative
  - OU normalisation bilan hépatique

**EFFETS SECONDAIRES:**
• **Réactions anaphylactoïdes:**
  - Dépendantes du débit de perfusion
  - Ralentir mais NE PAS ARRÊTER
  - Symptomatique si nécessaire

**SURVEILLANCE:**
• Paracétamolémie toutes les 4h
• Bilan hépatique quotidien
• TP, Facteur V
• Lactates
• Fonction rénale

**CRITÈRES DE GRAVITÉ:**
• Paracétamolémie élevée
• TP < 50%
• Encéphalopathie
• Acidose lactique
• Insuffisance rénale

**ORIENTATION:**
• Hospitalisation systématique
• Réanimation si hépatite fulminante
• Discuter transplantation hépatique si:
  - TP < 10%
  - Acidose métabolique persistante (pH < 7,3)
  - Encéphalopathie grade III-IV
  - Créatinine > 300 µmol/L

**⚠️ À SAVOIR:**
• Antidote efficace si < 8-10h
• Reste efficace après 24h
• Ne jamais retarder l'administration`
    },
    {
      id: 'opiaces',
      name: 'Opiacés',
      icon: Skull,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**INTOXICATION AUX OPIACÉS**

**SIGNES CLINIQUES:**

**SYNDROME OPIOÏDE TYPIQUE:**
• **Coma calme**
• **Bradycardie**
• **FR < 12/min**
• **Myosis bilatéral**

**PARTICULARITÉS:**

**Tramadol:**
• Syndrome sérotoninergique
• Coma hypertonique

**Codéine:**
• Variabilité métaboliseurs CYP2D6
• 10% métaboliseurs rapides (risque ↑)

**COMPLICATIONS:**

**NEUROLOGIQUES:**
• Apnée
• Encéphalopathie post-anoxique

**PULMONAIRES:**
• Œdème pulmonaire
• Sécrétions bronchiques
• Pneumopathie d'inhalation

**BIOLOGIE:**
• ECG: bradycardie isolée (chercher co-intoxication)
• Recherche toxiques urinaires (détecte opiacés naturels uniquement)

**ÉLÉMENTS DE GRAVITÉ:**
• Tentative de suicide/co-ingestion
• Insuffisance respiratoire sévère
• Encéphalopathie anoxique
• Pneumopathie d'inhalation

**TRAITEMENT - NALOXONE:**

**VOIE IV/IM/SC:**
• **Titration:**
  - 0,04 mg (adulte)
  - 0,01 mg/kg (enfant)
  - Toutes les minutes
  - **Objectif:** FR > 12/min

• **Si inefficace après 2 mg:**
  - Rechercher autre cause
  - Réévaluer diagnostic

**ENTRETIEN:**
• **IVSE:**
  - Moitié dose de titration par heure
  - Surveillance étroite

**VOIE INTRANASALE:**
• **0,9 mg par narine (1,8 mg total)**
• Si voie IV indisponible
• Spray nasal disponible

**SURVEILLANCE:**
• Fréquence respiratoire
• État de conscience
• Saturation
• ECG
• Recherche complications

**⚠️ À SAVOIR:**

**INEFFICACITÉ NALOXONE:**
• Buprénorphine (agoniste partiel)
• Tramadol (IRSNA)

**RISQUE SEVRAGE AIGU:**
• Chez usagers chroniques
• Signes: agitation, sueurs, mydriase
• Titration prudente

**DEMI-VIE:**
• Naloxone < Opiacés
• Risque récidive dépression respiratoire
• Surveillance prolongée (4-6h minimum)
• Re-titration si nécessaire

**ORIENTATION:**
• Transport médicalisé selon gravité
• SAU proche
• Réanimation si complications
• Surveillance hospitalière 6-24h`
    },
    {
      id: 'benzodiazepines',
      name: 'Benzodiazépines',
      icon: Pill,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-400/30',
      content: `**INTOXICATION AUX BENZODIAZÉPINES**

**MOLÉCULES CONCERNÉES:**
• Benzodiazépines
• Apparentés: Zopiclone, Zolpidem (Imidazopyridines)

**SIGNES CLINIQUES:**

**NEUROLOGIQUE:**
• **Syndrome myorelaxant:**
  - Coma calme
  - Hypotonie
  - Hyporéflexie
  - Pupilles intermédiaires

**CARDIO-PULMONAIRE:**
• **Insuffisance respiratoire périphérique:**
  - Myorelaxation pharyngée
  - Obstruction VAS
• Hémodynamique généralement préservée
• Possible hypotension et tachycardie

**COMPLICATIONS:**
• Pneumopathie d'inhalation
• Hypothermie

**BIOLOGIE:**
• ECG (anomalies si co-intoxication)
• Gaz du sang (acidose respiratoire)
• Ionogramme (recherche rhabdomyolyse)

**PRONOSTIC/GRAVITÉ:**
• Tentative suicide/co-ingestion dépresseurs centraux
• Sévérité du coma
• Présence de complications

**TRAITEMENT:**

**MESURES GÉNÉRALES:**
• **Charbon activé:** si ingestion < 1-2h et pas trouble vigilance
• Oxygène
• VVP
• Position latérale sécurité

**FLUMAZÉNIL (Anexate®):**

**INDICATIONS:**
• Intoxication isolée BZD/apparentés
• Défaillance neurologique nécessitant ventilation
• **EN L'ABSENCE DE:**
  - Contre-indications
  - Défaillance hémodynamique
  - Défaillance respiratoire

**POSOLOGIE ADULTE:**
• **Titration IV initiale:**
  - 0,1 mg toutes les 30 sec
  - Max 0,2 mg par injection
  - Dose cumulative max 1 mg
  - **Objectif:** réponse aux ordres simples

• **Si inefficace après 1-2 mg:**
  - Réévaluer étiologie
  - Autre cause que BZD

**POSOLOGIE ENFANT (> 1 an):**
• IV 10 µg/kg sur 15 sec
• Max 0,2 mg par injection
• Toutes les 2 min jusqu'à éveil
• Cumulative max 1 mg
• OU 0,05 mg/kg si poids < 20 kg

**ENTRETIEN:**
• IVSE continue
• Dose = dose titration initiale par heure

**SI INEFFICACE OU CONTRE-INDIQUÉ:**
• Traitement symptomatique des défaillances
• Envisager IOT si nécessaire

**⚠️ CONTRE-INDICATIONS FLUMAZÉNIL:**

**ABSOLUES:**
• **Polyintoxication avec pro-convulsivants:**
  - Tricycliques
  - ISRS
  - Autres
• **ATCD épilepsie**
• **Tableau clinique incompatible** avec intox BZD isolée:
  - Syndrome anticholinergique
  - Syndrome pyramidal
  - Autres anomalies

**SURVEILLANCE:**
• État de conscience
• Fréquence respiratoire
• Hémodynamique
• Recherche complications
• Durée surveillance: 4-6h minimum

**ORIENTATION:**
• Selon gravité et réponse traitement
• SAU pour surveillance
• Réanimation si défaillances
• Transport médicalisé si nécessaire

**⚠️ À SAVOIR:**
• Flumazénil demi-vie courte (1h)
• Risque récidive coma
• Surveillance prolongée nécessaire
• Inefficace sur barbituriques`
    },
    {
      id: 'aspirine',
      name: 'Aspirine (Salicylés)',
      icon: Pill,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**INTOXICATION AIGUË À L'ASPIRINE**

**DOSE TOXIQUE:**
• **Toxique:** > 10 g (adulte)
• **Létale:** > 450 mg/kg

**SIGNES CLINIQUES:**

**RESPIRATOIRE:**
• **Hyperventilation** (salicylémie > 300 mg/L)
• **Séquence:**
  1. Alcalose respiratoire
  2. Alcalose resp. + acidose métabolique
  3. Acidose mixte (dépression/épuisement)
• **OAP possible**

**NEUROLOGIQUE:**
• Acouphènes/hypoacousie
• Vertiges
• Troubles vigilance
• Coma
• Convulsions

**DIGESTIF:**
• Épigastralgies
• Vomissements (salicylémie > 250 mg/L)
• Hémorragies digestives rares
• Perforations rares

**HYPERTHERMIE:**
• Dysfonction mitochondriale
• Déshydratation extracellulaire

**BIOLOGIE:**

**SALICYLÉMIE:**
• **Valeur pronostique:**
  - Sévère: > 500 mg/L
  - Risque vital: > 700 mg/L
  - Épuration extrarénale: > 900 mg/L
• Utiliser **nomogramme de Done**
• Suivi si > 400 mg/L
• Doses répétées (absorption variable + gastroparésie)

**GAZ DU SANG:**
• Alcalose respiratoire
• Puis acidose métabolique (trou anionique ↑)
• Puis acidose mixte

**AUTRES:**
• Hypokaliémie (alcalose + pertes)
• Hypo/hyperglycémie
• Déshydratation extra/intracellulaire
• Hypernatrémie/hyperosmolarité
• Insuffisance rénale
• ↓ TP, ↓ fibrinogène
• CIVD (formes sévères)

**ÉLÉMENTS DE GRAVITÉ:**
• Dose ingérée
• Troubles vigilance
• Acidose métabolique
• Salicylémie > 500 mg/L
• Acouphènes (indiquent > 500 mg/L)

**TRAITEMENT:**

**MESURES GÉNÉRALES:**
• VVP
• Scope
• Oxygène

**CHARBON ACTIVÉ:**
• Si ingestion < 1-2h
• Dose: 1 g/kg (max 50 g)
• Puis 0,5 mg/kg toutes les 4h
• Pour formes moyennes/massives

**DÉSHYDRATATION:**
• Réhydratation IV adaptée

**HYPERTHERMIE:**
• Refroidissement externe
• Compresses froides
• Linges mouillés

**CHOC:**
• Expansion volémique
• +/- Noradrénaline

**ALCALINISATION URINAIRE:**

**Objectif:** Limiter réabsorption tubulaire

**BICARBONATE ISOTONIQUE 1,4%:**
• Bolus: 1 mmol/kg (6 mL/kg)
• Puis 100 mL/h

**BICARBONATE 4,2%:**
• Bolus: 2 mmol/kg (4 mL/kg)
• Avant IOT si indiquée

**SURVEILLANCE:**
• pH urinaire (objectif > 7,5)
• Ionogramme
• **Arrêt:** si salicylémie < 400 mg/L

**INTUBATION:**
• Si défaillance neuro ou respiratoire
• **Hyperventiler:**
  - Maintenir alcalose respiratoire
  - FR 30-35/min

**HYPOPROTHROMBINÉMIE:**
• Vitamine K

**AUTRES:**
• Correction hypokaliémie
• Surveillance glycémie (risque ↓ avec bicarbonates)

**ORIENTATION:**
• Transport médicalisé
• Réanimation/USI
• **Épuration extrarénale si:**
  - Salicylémie > 900 mg/L
  - Acidose sévère
  - Insuffisance rénale
  - OAP

**⚠️ À SAVOIR:**
• Acouphènes = marqueur de toxicité (> 500 mg/L)
• Alcalinisation = traitement clé
• Surveillance prolongée nécessaire
• Risque aggravation rapide`
    },
    {
      id: 'caustiques',
      name: 'Caustiques',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**INGESTION DE CAUSTIQUES**

**TYPES DE PRODUITS:**

**ACIDES FORTS (pH < 2):**
• **Mécanisme:** Nécrose de coagulation
• **Atteinte:** Principalement gastrique
• **Lésions:** Superficielles limitées
• **Complication:** Perforation précoce

**BASES FORTES (pH > 12):**
• **Mécanisme:** Nécrose de liquéfaction
  - Saponification lipoprotéines
• **Atteinte:** Principalement œsophagienne
• **Lésions:** Pénétration profonde possible
• **Complication:** Perforation tardive

**PRODUITS IRRITANTS (pH 7-10):**
• Symptômes digestifs mineurs
• Sensation de brûlure buccale
• Douleurs abdominales

**PRODUITS MOUSSANTS:**
• Symptômes digestifs/ORL
• Risque d'inhalation

**SIGNES CLINIQUES:**
• Brûlures buccales/pharyngées
• Dysphagie, odynophagie
• Douleurs épigastriques/rétrosternales
• Hypersialorrhée
• Vomissements (souvent sanglants)
• Détresse respiratoire (inhalation, œdème)
• Signes de péritonite (perforation)

**CRITÈRES DE GRAVITÉ:**
• **Ingestion massive:** > 150 mL acide/base forte
• **Signes péritonite/perforation:**
  - Défense abdominale
  - Contracture
  - Pneumopéritoine
• **Choc**
• **Hypoxie**
• **Acidose**
• **Signes psychiatriques:**
  - Confusion
  - Agitation

**CONDUITE À TENIR:**

**⚠️ À NE PAS FAIRE:**
• **Position couchée** (risque reflux)
• **Sonde nasogastrique**
• **Lavage gastrique**
• **Vomissements provoqués**
  - ⚠️ Risque aggravation lésions
• **Charbon activé**
  - Interfère avec FOGD

**À FAIRE:**

**MESURES GÉNÉRALES:**
• **À jeun strict (NPO)**
• VVP
• Monitoring

**RENSEIGNEMENTS:**
• Noter heure d'ingestion
• Nom du produit (garder emballage)
• **Appel Centre Antipoison**

**INTUBATION:**
• Si détresse respiratoire
• ⚠️ Risque IOT difficile
• Anticiper

**ANALGÉSIE:**
• Si douleurs importantes
• Éviter AINS (risque ulcère)

**EXAMEN ENDOSCOPIQUE:**
• **FOGD (Fibroscopie Œso-Gastro-Duodénale)**
• Urgence dans les 6-24h
• Évalue lésions
• Oriente traitement

**CLASSIFICATION ENDOSCOPIQUE:**
• **Grade 0:** Normal
• **Grade 1:** Érythème, œdème
• **Grade 2a:** Ulcérations superficielles localisées
• **Grade 2b:** Ulcérations profondes étendues
• **Grade 3a:** Nécroses focales
• **Grade 3b:** Nécroses étendues

**ORIENTATION:**

**SELON GRAVITÉ:**
• **Grade 0-1:** SAU, surveillance
• **Grade 2a:** Hospitalisation, surveillance
• **Grade 2b-3:** Réanimation, chirurgie
• **Perforation:** Chirurgie urgente

**SELON CLINIQUE:**
• Signes gravité → Réanimation
• Stable → Établissement avec FOGD

**TRANSPORT:**
• Médicalisé si gravité
• Position demi-assise

**SURVEILLANCE:**
• État hémodynamique
• Détresse respiratoire
• Douleurs
• Signes de perforation
• Surveillance prolongée

**COMPLICATIONS:**

**PRÉCOCES:**
• Perforation œsophage/estomac
• Médiastinite
• Péritonite
• Hémorragie digestive
• Détresse respiratoire

**TARDIVES:**
• Sténoses œsophagiennes
• Sténoses gastriques
• Cancer œsophage (long terme)

**⚠️ À SAVOIR:**
• Neutralisation CONTRE-INDIQUÉE (↑ chaleur)
• Pas de produit oral
• FOGD systématique si ingestion confirmée
• Suivi prolongé (sténoses)`
    },
    {
      id: 'organophosphores',
      name: 'Organophosphorés',
      icon: Skull,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      border: 'border-green-400/30',
      content: `**INTOXICATION AUX ORGANOPHOSPHORÉS**

**GÉNÉRALITÉS:**
• Insecticides (accidents agricoles)
• Intentionnels (tentatives suicide)
• Absorption: orale, respiratoire, cutanée
• **Cause principale décès:** HYPOXIE

**MÉCANISME:**
• Inhibition acétylcholinestérase
• Accumulation d'acétylcholine
• Hyperstimulation cholinergique
  - Muscarinique
  - Nicotinique
  - Centrale

**SIGNES CLINIQUES:**

**VARIABILITÉ SELON EXPOSITION:**

**INGESTION:**
• Symptômes digestifs initiaux

**CUTANÉE:**
• Début retardé
• Surveiller les contacts

**VAPEURS:**
• Triade classique:
  - Myosis
  - Rhinorrhée
  - Bronchoconstriction

**GAZ DE GUERRE:**
• Convulsions
• Apnée centrale
• (Rare avec insecticides)

**SIGNES GÉNÉRAUX:**
• Rhinorrhée
• Hypersialorrhée
• Hypersudation
• Incontinence fécale/urinaire
• Douleurs abdominales
• Asthénie

**NEUROLOGIQUE:**
• **Myosis**
• Tremblements
• Fasciculations
• Paralysie respiratoire
• Ataxie
• Confusion
• Coma
• Convulsions

**RESPIRATOIRE:**
• Bronchospasme
• Bronchorrhée
• Toux
• Œdème pulmonaire
• Apnée centrale

**CARDIOVASCULAIRE:**

**Effets muscariniques:**
• Bradycardie
• Hypotension

**Effets nicotiniques:**
• Tachycardie
• Hypertension
• Arythmies
• QT prolongé
• Torsades de Pointes
• FV

**BIOLOGIE:**

**Syndrome nicotinique:**
• Hyperglycémie
• Hypokaliémie
• Hypophosphorémie
• Acidose lactique

**Autres:**
• Dosage cholinestérases

**TRAITEMENT:**

**PROTECTION ÉQUIPES:**
• **EPI (Équipement Protection Individuelle):**
  - Gants
  - Blouse
  - Masque
  - Protection adaptée

**DÉCONTAMINATION:**
• Si nécessaire
• **Cutanée:** déshabillage complet
  - Lavage eau savonneuse
  - Rinçage abondant

**SYMPTOMATIQUE:**
• Oxygène
• IOT si nécessaire
• **Charbon activé:** si ingestion, pas CI

**CONVULSIONS/EME:**
• Algorithme standard
• **⚠️ CI fosphénytoïne** si troubles conduction
• Envisager midazolam + kétamine entretien

**ANTIDOTE - ATROPINE:**

**INDICATION:**
• Systématique si symptomatique

**POSOLOGIE:**

**ADULTE:**
• 2 mg IV
• Toutes les 5-10 min
• **Objectif:**
  - Tarissement sécrétions
  - Résolution bronchospasme

**ENFANT:**
• 0,05-0,1 mg/kg IV
• Toutes les 5-10 min
• **Objectif:**
  - Amélioration ventilation

**SIGNES SURDOSAGE:**
• Mydriase
• Agitation
• Délire

**ANTIDOTE - PRALIDOXIME (Contrathion®):**

**INDICATIONS SELON GRAVITÉ:**

**INTOXICATION MINEURE:**
• **Vapeurs:**
  - Myosis
  - Rhinorrhée
  - Bronchoconstriction légère
• **Liquide:**
  - Hypersalive
  - Fasciculations localisées

**Posologie:**
• Adulte: 200-400 mg IV/IM
• Enfant: 20-40 mg/kg IV sur 15 min

**INTOXICATION MODÉRÉE:**
• **Vapeurs:**
  - Comme mineure +
  - Bronchoconstriction/sécrétions modérées
• **Liquide:**
  - Comme mineure +
  - Nausées, diarrhée
  - Faiblesse généralisée

**Posologie:**
• Adulte:
  - Initial: 1 g IV/IM (jusqu'à 2 g)
  - Entretien: 5-8 mg/kg/h
  - OU 200-400 mg toutes les 4h
• Enfant:
  - Initial: 20-40 mg/kg IV sur 15 min
  - Entretien: 10 mg/kg/h

**INTOXICATION SÉVÈRE:**
• **Vapeurs ou Liquide:**
  - Perte de conscience
  - Convulsions
  - Fasciculations généralisées
  - Paralysie, apnée
  - Sécrétions généralisées
  - Myosis/défécation

**Posologie:**
• Adulte:
  - Initial: 2 g IV/IM
  - Entretien: 5-8 mg/kg/h
  - OU 200-400 mg toutes les 4h
• Enfant: Comme modérée

**SURVEILLANCE:**
• État neurologique
• Fonction respiratoire
• Hémodynamique
• Sécrétions
• ECG
• Ionogramme
• Cholinestérases

**ORIENTATION:**
• Transport médicalisé
• Réanimation/USI
• Surveillance prolongée (48-72h)
• Risque récidive

**⚠️ À SAVOIR:**
• Protection équipes primordiale
• Atropine en dose massive possible
• Pralidoxime efficace si < 24-48h
• Surveillance prolongée nécessaire
• Risque syndrome intermédiaire (24-96h)`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl border border-orange-400/30">
            <Skull className="w-8 h-8 text-orange-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Référentiels Intoxications Médicamenteuses</h1>
            <p className="text-[hsl(var(--text-dim))]">Protocoles toxicologie - SMUR 2025</p>
          </div>
        </div>
        
        <Alert className="bg-orange-500/10 border-orange-400/30">
          <Shield className="w-4 h-4 text-orange-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>Centre Antipoison Angers :</strong> Contact systématique pour guidance antidote et orientation
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Pill className="w-5 h-5 text-orange-400" />
            Protocoles Toxicologiques
          </CardTitle>
          <CardDescription>
            Sélectionnez une intoxication pour afficher le protocole détaillé
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {intoxications.map((intox) => {
                const Icon = intox.icon;
                const isLoading = aiLoading[intox.id];
                const aiResult = aiResults[intox.id];

                return (
                  <AccordionItem
                    key={intox.id}
                    value={intox.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                  >
                    <AccordionTrigger
                      className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                      data-testid={`accordion-trigger-${intox.id}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className={`p-2 ${intox.bg} rounded-lg border ${intox.border}`}>
                          <Icon className={`w-5 h-5 ${intox.color}`} />
                        </div>
                        <span className="font-semibold text-[hsl(var(--text))] text-left">
                          {intox.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 space-y-4">
                      <div className="space-y-4">
                        {/* Contenu médical */}
                        <div className="prose prose-invert max-w-none">
                          <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                            {intox.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return (
                                  <h3 key={idx} className="text-lg font-semibold text-cyan-400 mt-4 mb-2">
                                    {line.replace(/\*\*/g, '')}
                                  </h3>
                                );
                              }
                              if (line.startsWith('• ') || line.startsWith('- ')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 my-1">
                                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              if (line.startsWith('⚠️')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              return <p key={idx} className="my-1">{line}</p>;
                            })}
                          </div>
                        </div>

                        {/* Bouton Analyse AI */}
                        <Button
                          onClick={() => handleAiAnalysis(intox.id, intox.name, intox.content)}
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                          data-testid={`ai-button-${intox.id}`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse AI en cours...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analyse AI de ce protocole
                            </>
                          )}
                        </Button>

                        {/* Résultat AI */}
                        {aiResult && (
                          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Analyse AI
                            </h3>
                            <div className="prose prose-invert max-w-none">
                              <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${intox.id}`}>
                                {aiResult}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
